import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';

const FamilyGroupCard = ({ familyGroup, onEdit, onDelete, onViewMembers }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getSharedRestrictions = () => {
    if (!familyGroup?.members || familyGroup?.members?.length === 0) return [];
    
    // Find restrictions that are common to all family members
    const allRestrictions = familyGroup?.members?.flatMap(member => 
      member?.dietaryRestrictions || []
    );
    
    const restrictionCounts = allRestrictions?.reduce((acc, restriction) => {
      const key = `${restriction?.name}-${restriction?.type}`;
      acc[key] = (acc?.[key] || 0) + 1;
      return acc;
    }, {});

    return allRestrictions?.filter(restriction => {
      const key = `${restriction?.name}-${restriction?.type}`;
      return restrictionCounts?.[key] === familyGroup?.members?.length;
    })?.filter((restriction, index, self) => 
      index === self?.findIndex(r => r?.name === restriction?.name && r?.type === restriction?.type)
    );
  };

  const getDietaryChipColor = (type) => {
    switch (type?.toLowerCase()) {
      case 'allergy':
        return 'bg-error text-error-foreground';
      case 'intolerance':
        return 'bg-warning text-warning-foreground';
      case 'preference':
        return 'bg-secondary text-secondary-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const sharedRestrictions = getSharedRestrictions();

  return (
    <div className="bg-card border border-border rounded-lg shadow-warm hover:shadow-warm-md transition-warm">
      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
              <Icon name="Users" size={20} color="white" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">{familyGroup?.name}</h3>
              <p className="text-sm text-muted-foreground">
                {familyGroup?.members?.length || 0} members
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(familyGroup)}
              className="text-muted-foreground hover:text-foreground"
            >
              <Icon name="Edit2" size={16} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-muted-foreground hover:text-foreground"
            >
              <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={16} />
            </Button>
          </div>
        </div>

        {/* Member Avatars */}
        {familyGroup?.members && familyGroup?.members?.length > 0 && (
          <div className="flex items-center space-x-2 mb-3">
            <div className="flex -space-x-2">
              {familyGroup?.members?.slice(0, 4)?.map((member, index) => (
                <Image
                  key={member?.id}
                  src={member?.avatar}
                  alt={member?.avatarAlt}
                  className="w-8 h-8 rounded-full border-2 border-card object-cover"
                />
              ))}
              {familyGroup?.members?.length > 4 && (
                <div className="w-8 h-8 rounded-full border-2 border-card bg-muted flex items-center justify-center">
                  <span className="text-xs font-medium text-muted-foreground">
                    +{familyGroup?.members?.length - 4}
                  </span>
                </div>
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onViewMembers(familyGroup)}
              className="text-primary hover:text-primary/80 text-xs"
            >
              View All
            </Button>
          </div>
        )}

        {/* Shared Dietary Restrictions */}
        {sharedRestrictions?.length > 0 && (
          <div className="mb-3">
            <p className="text-xs text-muted-foreground mb-2">Shared Restrictions:</p>
            <div className="flex flex-wrap gap-1">
              {sharedRestrictions?.slice(0, 3)?.map((restriction, index) => (
                <span
                  key={index}
                  className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getDietaryChipColor(restriction?.type)}`}
                >
                  {restriction?.type === 'allergy' && <Icon name="AlertTriangle" size={10} />}
                  {restriction?.type === 'intolerance' && <Icon name="AlertCircle" size={10} />}
                  {restriction?.type === 'preference' && <Icon name="Heart" size={10} />}
                  <span>{restriction?.name}</span>
                </span>
              ))}
              {sharedRestrictions?.length > 3 && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                  +{sharedRestrictions?.length - 3} more
                </span>
              )}
            </div>
          </div>
        )}

        {/* Quick Stats */}
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <div className="flex items-center space-x-4">
            <span className="flex items-center space-x-1">
              <Icon name="Calendar" size={14} />
              <span>{familyGroup?.totalVisits || 0} group visits</span>
            </span>
            {familyGroup?.lastVisit && (
              <span className="flex items-center space-x-1">
                <Icon name="Clock" size={14} />
                <span>
                  {new Date(familyGroup.lastVisit)?.toLocaleDateString()}
                </span>
              </span>
            )}
          </div>
        </div>
      </div>
      {/* Expanded Details */}
      {isExpanded && (
        <div className="border-t border-border p-4 bg-muted/30">
          <div className="space-y-4">
            {/* All Members */}
            <div>
              <h4 className="text-sm font-medium text-foreground mb-2">Family Members</h4>
              <div className="space-y-2">
                {familyGroup?.members?.map((member) => (
                  <div key={member?.id} className="flex items-center justify-between p-2 bg-card rounded-md">
                    <div className="flex items-center space-x-2">
                      <Image
                        src={member?.avatar}
                        alt={member?.avatarAlt}
                        className="w-6 h-6 rounded-full object-cover"
                      />
                      <span className="text-sm font-medium">{member?.name}</span>
                      <span className="text-xs text-muted-foreground">{member?.email}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      {member?.dietaryRestrictions?.length > 0 && (
                        <span className="text-xs px-2 py-1 bg-muted rounded-full">
                          {member?.dietaryRestrictions?.length} restrictions
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Group Notes */}
            {familyGroup?.notes && (
              <div>
                <h4 className="text-sm font-medium text-foreground mb-2">Group Notes</h4>
                <div className="p-2 bg-card rounded-md">
                  <p className="text-sm text-muted-foreground">{familyGroup?.notes}</p>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center space-x-2 pt-2 border-t border-border">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEdit(familyGroup)}
                iconName="Edit2"
                iconPosition="left"
              >
                Edit Group
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onViewMembers(familyGroup)}
                iconName="Users"
                iconPosition="left"
              >
                Manage Members
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onDelete(familyGroup)}
                className="text-error hover:text-error/80 ml-auto"
              >
                <Icon name="Trash2" size={16} />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FamilyGroupCard;